import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Course } from '../../../shared/models/interfaces/course.interface';
import { MeService } from '../../../shared/services/me/me.service';
import { ProgressRingComponent } from '../../../shared/components/progress-ring/progress-ring.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import type { CourseContentSidebarLessonItem, CourseContentSidebarModuleItem } from '../../../shared/models/interfaces/sidebar.interface';
import { ContentType } from '../../../shared/models/enums/content-type.enum';
import type { CourseContent, HtmlContent, PdfContent, RichTextContent, VideoContent, VimeoContent, YoutubeContent, ActivityContent } from '../../../shared/models/interfaces/content.interface';
import type { Class } from '../../../shared/models/interfaces/module-class.interface';
import { ContentService } from '../../../shared/services/content/content.service';
import { ContentPdfComponent } from './components/content-pdf/content-pdf.component';
import { ContentHtmlComponent } from './components/content-html/content-html.component';
import { ContentRichTextComponent } from './components/content-rich-text/content-rich-text.component';
import { ContentVideoComponent } from './components/content-video/content-video.component';
import { ContentActivityComponent } from './components/content-activity/content-activity.component';
import { CourseContentService, CourseContentState, SidebarState } from '../../../shared/services/course-content/course-content.service';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { ACTIVITY_SCORE_SCALE, ActivityEvaluationService } from '../../../shared/services/activity-evaluation/activity-evaluation.service';
import type { ActivityEvaluationResult } from '../../../shared/models/interfaces/activity-evaluation.interface';
import { UserCourseService } from '../../../shared/services/user-course/user-course.service';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { getCrudErrors } from '../../../shared/utils/functions';
import { CertificateService } from '../../../shared/services/certificate/certificate.service';
import { PermissionService } from '../../../shared/services/permission/permission.service';
import { PermissionEnum, ResourceEnum } from '../../../../environments/shared-environment/permission';
import type { ContentProgressUpdate } from './content-progress-update';
import type { ContentPositionUpdate } from './content-position-update';
import type { Certificate } from '../../../shared/models/interfaces/certificate.interface';

@Component({
  selector: 'l-course-content',
  standalone: true,
  imports: [
    CommonModule,
    ProgressRingComponent,
    SidebarComponent,
    ContentPdfComponent,
    ContentHtmlComponent,
    ContentRichTextComponent,
    ContentVideoComponent,
    ContentActivityComponent,
    SpinnerComponent,
  ],
  templateUrl: './content.component.html',
})
export class CourseContentComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly meService: MeService,
    private readonly contentService: ContentService,
    private readonly courseContentService: CourseContentService,
    private readonly activityEvaluationService: ActivityEvaluationService,
    private readonly userCourseService: UserCourseService,
    private readonly toastService: ToastService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly certificateService: CertificateService,
    private readonly permissionService: PermissionService,
  ) {}

  //#endregion

  //#region Public Properties

  public course: Course | null = null;

  public isLoading: boolean = true;

  public notFound: boolean = false;

  public userCourseProgress: number = 0;

  public selectedModuleProgress: number = 0;

  public lessons: CourseContentSidebarLessonItem[] = [];

  public modules: CourseContentSidebarModuleItem[] = [];

  public selectedModuleId: string = '';

  public moduleTitle: string = 'Conteúdo do curso';

  public selectedLessonId: string = '';

  public selectedActivityClassId: string = '';

  public selectedContent: CourseContent | null = null;

  public isContentLoading: boolean = false;

  public isCourseComplete: boolean = false;

  public isCompletingCourse: boolean = false;

  public isDownloadingCertificate: boolean = false;

  public readonly contentType = ContentType;

  public progressClassId: string = '';

  public selectedResumePosition: number | null = null;

  private progressByClassId: Map<string, number> = new Map<string, number>();

  private persistedProgressByClassId: Map<string, number> = new Map<string, number>();

  private positionByClassId: Map<string, number> = new Map<string, number>();

  private lastAccessedClassId: string | null = null;

  private queuedProgress: ContentProgressUpdate | null = null;

  private queuedPosition: ContentPositionUpdate | null = null;

  private progressPersistTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly progressPersistDelayMs: number = 2000;

  private persistChain: Promise<void> = Promise.resolve();

  private readonly activityEvaluationByContentId: Map<string, ActivityEvaluationResult> = new Map<string, ActivityEvaluationResult>();

  //#endregion

  //#region Getters

  public get currentUserId(): string {
    return this.meService.getMe()?.id ?? '';
  }

  public get isLastModule(): boolean {
    if (!this.modules.length) return false;
    return this.modules.at(-1)?.id === this.selectedModuleId;
  }

  public get hasAssessmentInSelectedModule(): boolean {
    return !!this.findModuleAssessmentClass();
  }

  public get richTextBody(): string {
    if (!this.isRichTextContent(this.selectedContent)) {
      return '';
    }

    const body = this.selectedContent.content || this.selectedContent.htmlContent;

    return (body ?? '').trim();
  }

  public get assessmentScoreText(): string {
    const assessmentClass = this.findModuleAssessmentClass();

    if (!assessmentClass?.contentId) {
      return '0.0 / 10';
    }

    const savedResult = this.activityEvaluationByContentId.get(assessmentClass.contentId)
      ?? this.activityEvaluationService.getSavedResult(this.currentUserId, assessmentClass.contentId);

    if (!savedResult) {
      return '0.0 / 10';
    }

    return `${savedResult.userScore.toFixed(1)} / ${ACTIVITY_SCORE_SCALE}`;
  }

  //#endregion

  //#region Public Methods

  public async ngOnInit(): Promise<void> {
    const courseId = this.activatedRoute.snapshot.paramMap.get('courseId');

    if (!courseId) {
      this.setNotFoundState();
      return;
    }

    const userId = this.meService.getMe()?.id ?? '';
    const contentState = await this.courseContentService.load(courseId, userId);

    if (!contentState) {
      this.setNotFoundState();
      return;
    }

    await this.applyContentState(contentState);

    this.isLoading = false;
  }

  public ngOnDestroy(): void {
    void this.enqueueContentPersist();
  }

  public async onModuleSelected(moduleId: string): Promise<void> {
    if (!this.course) return;

    this.applySidebarState(this.courseContentService.getSidebarState(this.course, this.progressByClassId, moduleId));
    await this.selectFirstLesson();
  }

  public async onLessonSelected(lesson: CourseContentSidebarLessonItem): Promise<void> {
    await this.selectLessonById(lesson.id);
  }

  public async onAssessmentSelected(): Promise<void> {
    if (!this.selectedModuleId) {
      this.selectedContent = null;
      return;
    }

    this.selectedLessonId = '';
    this.isContentLoading = true;

    const assessmentClass = this.findModuleAssessmentClass();
    this.selectedActivityClassId = assessmentClass?.id ?? '';

    const moduleAssessment = assessmentClass?.contentId
      ? await this.contentService.getActivityById(assessmentClass.contentId)
      : null;

    this.selectedContent = moduleAssessment ?? null;
    this.isContentLoading = false;
  }

  public async onActivitySubmitted(result: ActivityEvaluationResult): Promise<void> {
    if (!this.course || !this.selectedActivityClassId || !this.currentUserId) return;

    this.activityEvaluationByContentId.set(result.contentId, result);

    if (!result.approved) {
      return;
    }

    try {
      const completionResult = await this.courseContentService.markClassComplete(
        this.currentUserId,
        this.selectedActivityClassId,
        this.course,
        this.progressByClassId,
      );

      this.progressByClassId = completionResult.progressByClassId;
      this.userCourseProgress = completionResult.userCourseProgress;
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
      return;
    }
    this.applySidebarState(
      this.courseContentService.getSidebarState(this.course, this.progressByClassId, this.selectedModuleId),
    );
  }

  public async onNextModule(): Promise<void> {
    const currentIndex = this.modules.findIndex((m) => m.id === this.selectedModuleId);
    const next = this.modules[currentIndex + 1];
    if (next) {
      await this.onModuleSelected(next.id);
    }
  }

  public async onRestartModule(resetModuleProgress: boolean = false): Promise<void> {
    if (!this.selectedModuleId) return;

    if (resetModuleProgress && this.course && this.currentUserId) {
      const completionResult = await this.courseContentService.resetModuleProgress(
        this.currentUserId,
        this.selectedModuleId,
        this.course,
        this.progressByClassId,
      );

      this.progressByClassId = completionResult.progressByClassId;
      this.userCourseProgress = completionResult.userCourseProgress;

      const assessmentClass = this.findModuleAssessmentClass();
      if (assessmentClass?.contentId) {
        this.activityEvaluationByContentId.delete(assessmentClass.contentId);
      }
    }

    await this.onModuleSelected(this.selectedModuleId);
  }

  public async onCertificate(): Promise<void> {
    if (!this.course || this.isDownloadingCertificate) {
      return;
    }

    if (this.userCourseProgress < 100) {
      this.toastService.warn('Conclua o curso para acessar o certificado.', 'Atenção');
      return;
    }

    this.isDownloadingCertificate = true;

    try {
      const certificate = await this.certificateService.findByCourse(this.course.id)
        ?? await this.issueCertificate(this.course.id);

      if (!certificate) {
        this.toastService.warn('O certificado deste curso ainda não está disponível.', 'Atenção');
        return;
      }

      await this.certificateService.download(certificate);
    } catch (error: unknown) {
      const message = error instanceof Error && error.message ? error.message : 'Não foi possível baixar o certificado.';
      this.toastService.error(message, 'Oops...');
    } finally {
      this.isDownloadingCertificate = false;
    }
  }

  public async onCompleteCourse(): Promise<void> {
    if (!this.course || this.isCompletingCourse || this.isCourseComplete) {
      return;
    }

    if (this.userCourseProgress < 100) {
      this.toastService.warn(
        'Conclua todas as aulas e avaliações antes de finalizar o curso.',
        'Atenção',
      );
      return;
    }

    this.isCompletingCourse = true;

    try {
      const enrollment = await this.userCourseService.completeCourse(this.course.id);
      this.userCourseProgress = enrollment.progress;
      this.isCourseComplete = !!enrollment.completedAt;
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    } finally {
      this.isCompletingCourse = false;
    }
  }

  public onContentProgress(update: ContentProgressUpdate): Promise<void> {
    const classId = update.classId || this.progressClassId;

    if (!classId || !this.course || !this.currentUserId) {
      return Promise.resolve();
    }

    const next = Math.min(100, Math.max(0, Math.round(update.progress)));
    const displayed = this.progressByClassId.get(classId) ?? 0;

    if (next <= displayed) {
      return Promise.resolve();
    }

    const progressByClassId = new Map(this.progressByClassId);
    progressByClassId.set(classId, next);
    this.progressByClassId = progressByClassId;
    this.refreshSidebar();

    this.queuedProgress = { classId, progress: next };

    if (next >= 100) {
      return this.enqueueContentPersist();
    }

    this.schedulePersist();

    return Promise.resolve();
  }

  public onContentPosition(update: ContentPositionUpdate): void {
    const classId = update.classId || this.progressClassId;

    if (!classId || !this.course || !this.currentUserId || !Number.isFinite(update.position) || update.position < 0) return;

    const positionByClassId = new Map(this.positionByClassId);
    positionByClassId.set(classId, update.position);
    this.positionByClassId = positionByClassId;
    this.queuedPosition = { classId, position: update.position };
    this.schedulePersist();
  }

  public isVideoContent(content: CourseContent | null): content is VideoContent | YoutubeContent | VimeoContent {
    if (!content) return false;

    return content.type === ContentType.VIDEO || content.type === ContentType.YOUTUBE || content.type === ContentType.VIMEO;
  }

  public isHtmlContent(content: CourseContent | null): content is HtmlContent {
    return !!content && content.type === ContentType.HTML;
  }

  public isRichTextContent(content: CourseContent | null): content is RichTextContent {
    return !!content && content.type === ContentType.RICH_TEXT;
  }

  public isPdfContent(content: CourseContent | null): content is PdfContent {
    return !!content && content.type === ContentType.PDF;
  }

  public isActivityContent(content: CourseContent | null): content is ActivityContent {
    return !!content && content.type === ContentType.ACTIVITY;
  }

  //#endregion

  //#region Private Methods

  private async applyContentState(contentState: CourseContentState): Promise<void> {
    this.course = contentState.course;
    this.userCourseProgress = contentState.userCourseProgress;
    const enrollment = this.currentUserId
      ? await this.userCourseService.getUserCourse(this.currentUserId, contentState.course.id)
      : undefined;
    this.isCourseComplete = !!enrollment?.completedAt;
    this.progressByClassId = contentState.progressByClassId;
    this.persistedProgressByClassId = new Map(contentState.progressByClassId);
    this.positionByClassId = new Map(contentState.positionByClassId);
    this.lastAccessedClassId = contentState.lastAccessedClassId;
    this.applySidebarState(contentState.sidebarState);
    await this.loadAssessmentResultsForCurrentCourse();
    await this.selectResumeLesson();
  }

  private applySidebarState(sidebarState: SidebarState): void {
    this.modules = sidebarState.modules;
    this.lessons = sidebarState.lessons;
    this.selectedModuleId = sidebarState.selectedModuleId;
    this.selectedModuleProgress = sidebarState.selectedModuleProgress;
    this.moduleTitle = sidebarState.moduleTitle;
  }

  private async loadAssessmentResultsForCurrentCourse(): Promise<void> {
    this.activityEvaluationByContentId.clear();

    const assessmentClasses = this.course?.modules
      ?.flatMap((module) => module.classes ?? [])
      .filter((moduleClass) => moduleClass.type === ContentType.ACTIVITY && !!moduleClass.contentId) ?? [];

    for (const assessmentClass of assessmentClasses) {
      const savedResult = this.activityEvaluationService.loadLatest
        ? await this.activityEvaluationService.loadLatest(this.currentUserId, assessmentClass.contentId!)
        : this.activityEvaluationService.getSavedResult(this.currentUserId, assessmentClass.contentId!);

      if (savedResult) {
        this.activityEvaluationByContentId.set(assessmentClass.contentId!, savedResult);
      }
    }
  }

  private setNotFoundState(): void {
    this.notFound = true;
    this.isLoading = false;
  }

  private async selectFirstLesson(): Promise<void> {
    if (!this.lessons.length) {
      this.selectedLessonId = '';
      this.selectedContent = null;
      return;
    }

    await this.selectLessonById(this.lessons[0].id);
  }

  private async selectResumeLesson(): Promise<void> {
    const resumeClass = this.findResumeClass();

    if (!this.course || !resumeClass) {
      await this.selectFirstLesson();
      return;
    }

    this.applySidebarState(this.courseContentService.getSidebarState(this.course, this.progressByClassId, resumeClass.moduleId));
    await this.selectLessonById(resumeClass.id);
  }

  private findResumeClass(): Class | null {
    if (!this.course) return null;

    const lessons = [...(this.course.modules ?? [])]
      .sort((first, second) => first.order - second.order)
      .flatMap((module) => [...(module.classes ?? [])].sort((first, second) => first.order - second.order))
      .filter((moduleClass) => moduleClass.type !== ContentType.ACTIVITY);
    const progressOf = (moduleClass: Class): number => this.progressByClassId.get(moduleClass.id) ?? 0;

    return lessons.find((moduleClass) => moduleClass.id === this.lastAccessedClassId && progressOf(moduleClass) < 100)
      ?? lessons.find((moduleClass) => progressOf(moduleClass) < 100)
      ?? null;
  }

  private async selectLessonById(lessonId: string): Promise<void> {
    this.selectedLessonId = lessonId;

    const selectedClass = this.findSelectedClass(lessonId);

    if (!selectedClass?.contentId) {
      this.selectedContent = null;
      return;
    }

    const hadOpenContent = !!this.progressClassId;
    this.isContentLoading = true;

    if (hadOpenContent) {
      this.changeDetectorRef.detectChanges();
    }

    const content = await this.contentService.getById(selectedClass.contentId) ?? null;
    await this.enqueueContentPersist();
    this.selectedResumePosition = this.positionByClassId.get(selectedClass.id) ?? null;
    this.lastAccessedClassId = selectedClass.id;
    this.selectedContent = content;
    this.progressClassId = content ? selectedClass.id : '';
    this.isContentLoading = false;

    if (this.isHtmlContent(content)) {
      await this.persistAccessedHtml(selectedClass.id);
    }
  }

  private async persistAccessedHtml(classId: string): Promise<void> {
    if ((this.progressByClassId.get(classId) ?? 0) >= 100) return;
    if (!this.course || !this.currentUserId) return;

    try {
      const result = await this.courseContentService.markClassComplete(
        this.currentUserId,
        classId,
        this.course,
        this.progressByClassId,
      );
      this.progressByClassId = result.progressByClassId;
      this.persistedProgressByClassId = new Map(result.progressByClassId);
      this.userCourseProgress = result.userCourseProgress;
      this.refreshSidebar();
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    }
  }

  private enqueueContentPersist(): Promise<void> {
    const run = this.persistChain
      .then(() => this.flushContentProgress())
      .then(() => this.flushContentPosition());
    this.persistChain = run.catch(() => undefined);
    return run;
  }

  private async flushContentProgress(): Promise<void> {
    if (this.progressPersistTimer) {
      clearTimeout(this.progressPersistTimer);
      this.progressPersistTimer = null;
    }

    const queued = this.queuedProgress;
    this.queuedProgress = null;

    if (!queued || !this.course || !this.currentUserId) {
      return;
    }

    const persisted = this.persistedProgressByClassId.get(queued.classId) ?? 0;
    if (queued.progress <= persisted) {
      return;
    }

    try {
      const result = await this.courseContentService.updateClassProgress(
        this.currentUserId,
        queued.classId,
        this.course,
        this.persistedProgressByClassId,
        queued.progress,
        this.userCourseProgress,
      );

      this.persistedProgressByClassId = result.progressByClassId;
      this.userCourseProgress = result.userCourseProgress;

      const confirmed = result.progressByClassId.get(queued.classId) ?? queued.progress;
      const displayed = this.progressByClassId.get(queued.classId) ?? 0;

      if (confirmed > displayed) {
        const progressByClassId = new Map(this.progressByClassId);
        progressByClassId.set(queued.classId, confirmed);
        this.progressByClassId = progressByClassId;
        this.refreshSidebar();
      }
    } catch (error) {
      const latestQueued = this.queuedProgress as ContentProgressUpdate | null;
      const hasNewer = !!latestQueued && latestQueued.classId === queued.classId && latestQueued.progress > persisted;

      if (!hasNewer) {
        const progressByClassId = new Map(this.progressByClassId);
        progressByClassId.set(queued.classId, persisted);
        this.progressByClassId = progressByClassId;
        this.refreshSidebar();
      }

      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    }
  }

  private async flushContentPosition(): Promise<void> {
    const queued = this.queuedPosition;
    this.queuedPosition = null;

    if (!queued || !this.course || !this.currentUserId) {
      return;
    }

    try {
      await this.courseContentService.updateClassPosition(
        this.currentUserId,
        queued.classId,
        this.course,
        this.persistedProgressByClassId.get(queued.classId) ?? 0,
        queued.position,
      );
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    }
  }

  private schedulePersist(): void {
    if (this.progressPersistTimer) return;

    this.progressPersistTimer = setTimeout(() => {
      this.progressPersistTimer = null;
      void this.enqueueContentPersist();
    }, this.progressPersistDelayMs);
  }

  private refreshSidebar(): void {
    if (!this.course) return;

    this.applySidebarState(
      this.courseContentService.getSidebarState(this.course, this.progressByClassId, this.selectedModuleId),
    );
  }

  private findSelectedClass(lessonId: string): Class | null {
    const selectedModule = this.course?.modules?.find((module) => module.id === this.selectedModuleId);
    if (!selectedModule) return null;

    return selectedModule.classes?.find((moduleClass) => moduleClass.id === lessonId) ?? null;
  }

  private findModuleAssessmentClass(): Class | null {
    const selectedModule = this.course?.modules?.find((module) => module.id === this.selectedModuleId);
    if (!selectedModule) return null;

    return selectedModule.classes?.find((moduleClass) => moduleClass.type === ContentType.ACTIVITY) ?? null;
  }

  private async issueCertificate(courseId: string): Promise<Certificate | undefined> {
    if (!this.isCourseComplete || !this.permissionService.has(ResourceEnum.CERTIFICATES, PermissionEnum.CREATE)) {
      return undefined;
    }

    return await this.certificateService.issue(courseId);
  }

  //#endregion

}
